import React from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import './App.css';
import theme from './theme';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import PatientForm from './pages/PatientForm';
import PatientList from './pages/PatientList';
import BirthdayList from './pages/BirthdayList';
import ReminderList from './pages/ReminderList';
import PatientDetails from './pages/PatientDetails';
import VisitationForm from './pages/VisitationForm';
import VisitationDetails from './pages/VisitationDetails';
import TreatmentForm from './pages/TreatmentForm';
import TreatmentDetails from './pages/TreatmentDetails';
import SessionForm from './pages/SessionForm';
import SessionDetails from './pages/SessionDetails';
import MedicineManagement from './pages/MedicineManagement';
import PublicPatientRegistration from './pages/PublicPatientRegistration';
import InventoryList from './pages/InventoryList';
import InventoryForm from './pages/InventoryForm';
import BatchList from './pages/BatchList';
import MedicineSales from './pages/MedicineSales';
import Lobby from './pages/Lobby';
import Billing from './pages/Billing';
import TodayPatients from './pages/TodayPatients';

// Protected Route Component
function ProtectedRoute({ children, requireDoctor }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (requireDoctor && user.role !== 'Doctor') {
    return <div className="error-message">Access denied. Doctor privileges required.</div>;
  }

  return children;
}

// Main Layout with Navigation
function MainLayout() {
  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
  };

  const isDoctor = user?.role === 'Doctor';

  return (
    <div className="App">
      <nav className="navbar">
        <div className="navbar-content">
          <div className="navbar-logo">
            <img src="/logo.jpg" alt="Glow With SuperDoc" className="app-logo" />
          </div>
          <nav>
            {isDoctor && <NavLink to="/">Dashboard</NavLink>}
            <NavLink to="/lobby">Waiting Area</NavLink>
            <NavLink to="/today-patients">Today's Patients</NavLink>
            <NavLink to="/patients">Patient List</NavLink>
            <NavLink to="/birthdays">Birthday List</NavLink>
            {/* <NavLink to="/reminders">Reminder</NavLink> */}
            <NavLink to="/add-patient">Add Patient</NavLink>
            <NavLink to="/medicines">Medicines</NavLink>
            <NavLink to="/inventory">Inventory</NavLink>
            <NavLink to="/sales">Sales</NavLink>
            <NavLink to="/billing">Billing</NavLink>
          </nav>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <span style={{ color: 'white', fontSize: '0.9rem' }}>
              {user?.name} ({user?.role})
            </span>
            <button
              onClick={handleLogout}
              className="btn btn-secondary"
              style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
            >
              Logout
            </button>
          </div>
        </div>
      </nav>

      <div className="container">
        <Routes>
          <Route
            path="/"
            element={
              isDoctor ? (
                <ProtectedRoute requireDoctor={true}>
                  <Dashboard />
                </ProtectedRoute>
              ) : (
                <Navigate to="/patients" replace />
              )
            }
          />
          <Route path="/add-patient" element={<PatientForm />} />
          <Route path="/patients" element={<PatientList />} />
          <Route path="/birthdays" element={<BirthdayList />} />
          <Route path="/reminders" element={<ReminderList />} />
          <Route path="/patient/:patientId" element={<PatientDetails />} />
          <Route
            path="/visitation/new/:patientId"
            element={
              <ProtectedRoute requireDoctor={true}>
                <VisitationForm />
              </ProtectedRoute>
            }
          />
          <Route path="/visitation/:visitationId" element={<VisitationDetails />} />
          <Route
            path="/treatment/new/:patientId"
            element={
              <ProtectedRoute requireDoctor={true}>
                <TreatmentForm />
              </ProtectedRoute>
            }
          />
          <Route path="/treatment/:treatmentId" element={<TreatmentDetails />} />
          <Route
            path="/session/new/:treatmentId"
            element={
              <ProtectedRoute requireDoctor={true}>
                <SessionForm />
              </ProtectedRoute>
            }
          />
          <Route path="/session/:sessionId" element={<SessionDetails />} />
          <Route path="/medicines" element={<MedicineManagement />} />
          <Route path="/inventory" element={<InventoryList />} />
          <Route path="/inventory/new" element={<InventoryForm />} />
          <Route path="/inventory/edit/:id" element={<InventoryForm />} />
          <Route path="/inventory/:inventoryId/batches" element={<BatchList />} />
          <Route path="/sales" element={<MedicineSales />} />
          <Route path="/patient/:patientId/sell-medicines" element={<MedicineSales />} />
          <Route path="/lobby" element={<Lobby />} />
          <Route path="/today-patients" element={<TodayPatients />} />
          <Route path="/billing" element={<Billing />} />
        </Routes>
      </div>
    </div>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <AuthProvider>
          <Router>
            <Routes>
              <Route path="/register" element={<PublicPatientRegistration />} />
              <Route path="/login" element={<Login />} />
              <Route
                path="/*"
                element={
                  <ProtectedRoute>
                    <MainLayout />
                  </ProtectedRoute>
                }
              />
            </Routes>
          </Router>
        </AuthProvider>
      </LocalizationProvider>
    </ThemeProvider>
  );
}

export default App;
